<template>
    <div>
        <b-card no-body>
            <b-card-header header-bg-variant="primary">
                <span class="lead">{{ $tc('tasks.title', taskListCount) }}</span>&nbsp;
                <span class="badge badge-info">{{ taskListCount }}</span>
                <b-button variant="info" class="float-right pb-0" v-b-tooltip.hover.d250="$t('files.context.createTaskGroup')" @click="createTaskList()" :disabled="!projectPermissions.includes('CREATE_TASK_LIST')"><icon name="plus"></icon></b-button>
            </b-card-header>
            <b-card-body>
                <b-table 
                    :items="taskLists"
                    :fields="tableFields"
                    show-empty
                    hover
                    @row-clicked="onRowClicked">
                    <template slot="empty">
                        <span>{{ $t('tasks.noList') }}</span>
                    </template>
                    <template slot="row-actions" slot-scope="data">
                        <b-button size="sm" variant="outline-danger" class="float-right pb-0" v-b-tooltip.hover.d250="$t('files.context.deleteTaskGroup')"
                                  @click.stop="deleteTaskList(data.item.id)" :disabled="!projectPermissions.includes('DELETE_TASK_LIST')"><icon name="trash"></icon>
                        </b-button>
                        <b-button size="sm" class="float-right pb-0" v-b-tooltip.hover.d250="$t('files.context.editTaskGroup')"
                                  @click.stop="editTaskListTitle(data.item)" :disabled="!projectPermissions.includes('UPDATE_TASK_LIST')"><icon name="pencil"></icon>
                        </b-button>
                    </template>
                </b-table>
            </b-card-body>
        </b-card>

        <!-- Task list modal dialog -->
        <b-modal
            ref="taskListDialog"
            :title="taskListToEdit ? $t('tasks.editTaskGroup') : $t('tasks.newTaskGroup')"
            :ok-title="taskListToEdit ? $t('tasks.saveChanges') : $t('tasks.addTaskGroup')"
            ok-variant="outline-info"
            :cancel-title="$t('general.cancel')"
            cancel-variant="outline-secondary"
            header-bg-variant="secondary"
            header-text-variant="white"
            centered
            no-fade
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
            @ok="taskListDialog_okClicked"
            @shown="taskListDialog_onShown"
            @cancel="taskListDialog_cancelClicked"
            >
            <b-form-group v-if="inputLimits">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('tasks.taskGroupName') }}:</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{dialogModel.name.length}} / {{inputLimits.taskListName}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-input
                    id="txtNewTaskListName"
                    ref="newTaskListNameInput"
                    type="text"
                    :maxlength="inputLimits.taskListName"
                    v-model="dialogModel.name"
                    @keyup.enter.native="taskListDialog_okClicked">
                </b-form-input>
            </b-form-group>
        </b-modal>
    </div>
</template>

<script>
import toastr from 'toastr';
import {mapState} from 'vuex';
import i18n from '@/i18n.bootstrap';

export default {
    name: 'task-list',

    props: ['taskLists'],

    data() {
        return {
            tableFields: [
                { key: 'name', sortable: true, tdClass: 'global-cursor-pointer', thStyle: {width: '75%'} },
                { key: 'row-actions', label: '', thStyle: {width: '25%'} }
            ],

            // new task list modal dialog support
            dialogModel: {
                name: ''
            },

            taskListToEdit: undefined
        }
    },

    computed: {
        taskListCount() {
            if (!this.taskLists) {
                return 0;
            }

            return this.taskLists.length || 0;
        },
        ...mapState('system', ['inputLimits']),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    mounted() {
        
    },

    methods: {
        createTaskList() {
            this.$refs.taskListDialog.show();
        },

        editTaskListTitle(taskList) {
            this.taskListToEdit = taskList;
            this.$refs.taskListDialog.show();
        },
        
        deleteTaskList(id) {
            const deleteTaskListAction = () => {
                this.$store.dispatch('projects/deleteTaskList', {
                    listId: id
                }).then(() => {
                    if (this.taskLists.length > 0) {
                        this.$root.$emit('project-tasks::task-list-selected', { taskList: this.taskLists[0] });
                    } else {
                        this.$root.$emit('project-tasks::task-list-selected', { taskList: null });
                    }
                }).catch(() => {
                    toastr.error(i18n.t('tasks.messages.action.errorDeleteTaskGroup'));
                });
            };

            this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: deleteTaskListAction },
                    { text: i18n.t('general.no') }
                ]
            });
        },

        onRowClicked(item /* , index, event */) {
            this.$root.$emit('project-tasks::task-list-selected', { taskList: item });
        },

        taskListDialog_onShown() {
            if (!this.taskListToEdit) {
                this.dialogModel.name = '';
            } else {
                this.dialogModel.name = this.taskListToEdit.name;
            }

            this.$refs.newTaskListNameInput.focus();
        },

        taskListDialog_okClicked(event) {
            // prevents the modal from closing
            event.preventDefault();
            
            if (this.dialogModel.name && this.dialogModel.name !== '') {
                if (!this.taskListToEdit) {
                    this.$store.dispatch('projects/createTaskList', {
                        projectId: this.$route.params.projectId,
                        name: this.dialogModel.name
                    }).then(() => {
                        let newTaskList = this.taskLists.find(item => item.name.toLowerCase() === this.dialogModel.name.toLowerCase());
                        this.$root.$emit('project-tasks::task-list-selected', { taskList: newTaskList });

                        this.taskListToEdit = undefined;
                        this.$refs.taskListDialog.hide();
                    });
                } else {
                    this.$store.dispatch('projects/editTaskListName', {
                        id: this.taskListToEdit.id,
                        projectId: this.$route.params.projectId,
                        name: this.dialogModel.name
                    }).then(() => {
                        this.taskListToEdit = undefined;
                        this.$refs.taskListDialog.hide();
                    });
                }
            } else {
                toastr.warning(i18n.t('tasks.messages.validation.taskGroupName'));
            }
        },

        taskListDialog_cancelClicked() {
            this.taskListToEdit = undefined;
        }
    }
};
</script>

<style>
</style>
